import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["borderColor", "borderStyle", "borderWidth", "displayTotalAvatarCount", "hasMoreHref", "maxAvatars", "size", "truncateAvatarListInTooltip", "totalAvatarCountColor"],
  _excluded2 = ["companyId", "displayName", "domain", "email", "fileManagerKey", "href", "hubSpotUserEmail", "lookup", "marketingEventId", "productId", "type", "vid"];
import { Component } from 'react';
import devLogger from 'react-utils/devLogger';
import { makeLookupObject } from './lib/makeLookupObject';
import { AvatarRequest } from './AvatarRequest';
import { MoreResultsIndicator } from './AvatarDisplay/MoreResultsIndicator';
import { AVATAR_LIST_SIZES_MARGIN_OFFSET, DEFAULT_PROPS, MULTI_AVATAR_DEFAULT_BORDER_COLOR, MULTI_AVATAR_DEFAULT_BORDER_STYLE } from './Constants';
import { MULTI_AVATAR_DEFAULT_BORDER_WIDTH } from './Constants';
import { getBorderProps, getMarginProps } from './lib/stylePropHelpers';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const evaluatePropWarnings = ({
  maxAvatars,
  hasMoreHref
}) => {
  if (maxAvatars && maxAvatars < 2) {
    devLogger.warn({
      message: '[Avatar] maxAvatars needs to be greater than or equal to 2',
      key: 'Avatar-maxAvatarsWarning'
    });
  }
  if (hasMoreHref && !maxAvatars) {
    devLogger.warn({
      message: '[Avatar] hasMoreHref will only work if maxAvatars is set',
      key: 'Avatar-hasMoreHrefWarning'
    });
  }
  return null;
};
export class Avatar extends Component {
  componentDidMount() {
    evaluatePropWarnings(this.props);
  }
  componentDidUpdate() {
    evaluatePropWarnings(this.props);
  }
  getDisplayNameFromLookup(lookup) {
    const {
      hubSpotUserEmail
    } = this.props;

    // If the hubSpotUserEmail is set, we don't want to show the displayName
    // This is legacy logic from the original UIAvatar library.
    if (hubSpotUserEmail) return undefined;
    const proxyDisplayName = lookup.secondaryIdentifier || lookup.primaryIdentifier;
    return typeof proxyDisplayName === 'string' ? proxyDisplayName : undefined;
  }
  renderAvatarList(lookup) {
    const _this$props = this.props,
      {
        borderColor,
        borderStyle,
        borderWidth,
        displayTotalAvatarCount,
        hasMoreHref,
        maxAvatars,
        size,
        truncateAvatarListInTooltip,
        totalAvatarCountColor
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);

    // if maxAvatars is not set, use the length of the lookup array and show all.
    const maxAvatarsProxy = maxAvatars || lookup.length;
    const hasHiddenAvatars = lookup.length > maxAvatarsProxy;

    // Lists of avatars have defaulted border styles
    const borderColorProxy = borderColor || MULTI_AVATAR_DEFAULT_BORDER_COLOR;
    const borderStyleProxy = borderStyle || MULTI_AVATAR_DEFAULT_BORDER_STYLE;
    const borderWidthProxy = borderWidth || MULTI_AVATAR_DEFAULT_BORDER_WIDTH;
    const offset = displayTotalAvatarCount ? 0 : -1;
    const newLookupList = hasHiddenAvatars ? lookup.slice(0, maxAvatarsProxy + offset) : lookup.slice(0);
    return /*#__PURE__*/_jsxs(Flex, Object.assign({
      flexDirection: "row-reverse",
      justifyContent: "start",
      "data-test-id": "AvatarMultiOrSingle-avatarListWrapper"
    }, getMarginProps(rest), {
      children: [hasHiddenAvatars ? /*#__PURE__*/_jsx(MoreResultsIndicator, {
        borderColor: borderColorProxy,
        borderStyle: borderStyleProxy,
        borderWidth: borderWidthProxy,
        totalAvatarCountColor: totalAvatarCountColor,
        displayTotalAvatarCount: displayTotalAvatarCount,
        hasMoreHref: hasMoreHref,
        lookup: lookup,
        marginInlineStart: AVATAR_LIST_SIZES_MARGIN_OFFSET[size],
        maxAvatars: maxAvatarsProxy,
        size: size,
        truncateAvatarListInTooltip: truncateAvatarListInTooltip,
        offset: offset
      }) : null, newLookupList.reverse().map((lookupObj, i) => {
        const internalLookup = makeLookupObject(lookupObj);
        const displayName = lookupObj.displayName || this.getDisplayNameFromLookup(internalLookup);
        const marginInlineStart = i === newLookupList.length - 1 ? undefined : AVATAR_LIST_SIZES_MARGIN_OFFSET[size];
        const src = lookupObj.src;
        return this.renderSingleAvatar(internalLookup, {
          borderProps: {
            borderColor: borderColorProxy,
            borderStyle: borderStyleProxy,
            borderWidth: borderWidthProxy
          },
          dataTestId: `${this.props['data-test-id'] || 'AvatarMultiOrSingle-avatarListItem'}-${i}`,
          displayName,
          href: lookupObj.href,
          marginProps: {
            marginInlineStart
          },
          src
        }, i);
      })]
    }));
  }
  renderSingleAvatar(lookup, options = {}, key) {
    const {
      childMarginInlineStart,
      children,
      childTruncateLength,
      fetchAvatars,
      isEditable,
      onClick,
      showTooltip,
      size,
      socialNetwork,
      src,
      tooltipPlacement,
      tooltipTitle,
      type
    } = this.props;
    const proxyType = type || lookup.type;
    const {
      marginProps,
      borderProps,
      displayName,
      href
    } = options;

    // child props are only used for single avatars
    const childProps = typeof key === 'undefined' ? {
      children,
      childMarginInlineStart,
      childTruncateLength
    } : {};
    return /*#__PURE__*/_jsx(AvatarRequest, Object.assign({}, Object.assign({
      displayName,
      fetchAvatars,
      href,
      isEditable,
      lookup,
      onClick,
      showTooltip,
      size,
      socialNetwork,
      src: src || options.src,
      tooltipPlacement,
      tooltipTitle,
      type: proxyType,
      'data-test-id': options.dataTestId
    }, childProps, marginProps, borderProps)), key);
  }
  render() {
    const _this$props2 = this.props,
      {
        companyId,
        displayName,
        domain,
        email,
        fileManagerKey,
        href,
        hubSpotUserEmail,
        lookup,
        marketingEventId,
        productId,
        type,
        vid
      } = _this$props2,
      rest = _objectWithoutPropertiesLoose(_this$props2, _excluded2);
    if (lookup && Array.isArray(lookup)) {
      return this.renderAvatarList(lookup);
    }
    const proxyLookup = makeLookupObject(Object.assign({
      companyId,
      domain,
      email,
      fileManagerKey,
      hubSpotUserEmail,
      marketingEventId,
      productId,
      type,
      vid
    }, lookup || {}));
    const marginProps = getMarginProps(rest);
    const borderProps = getBorderProps(rest);
    return this.renderSingleAvatar(proxyLookup, {
      borderProps,
      dataTestId: this.props['data-test-id'],
      displayName: displayName || this.getDisplayNameFromLookup(proxyLookup),
      href: href || (lookup === null || lookup === void 0 ? void 0 : lookup.href),
      marginProps
    });
  }
}
Avatar.defaultProps = DEFAULT_PROPS;
Avatar.displayName = 'Avatar';